'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';

export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'free';

interface SubscriptionData {
  isPremium: boolean;
  status: SubscriptionStatus;
  planId?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

interface PremiumContextType extends SubscriptionData {
  isLoading: boolean;
  refreshSubscription: () => Promise<void>;
}

const PremiumContext = createContext<PremiumContextType | undefined>(undefined);

export function PremiumProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status: sessionStatus } = useSession();
  const [subscription, setSubscription] = useState<SubscriptionData>({
    isPremium: false,
    status: 'free',
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubscription = async () => {
    if (sessionStatus === 'loading') return;

    if (!session?.user) {
      setSubscription({ isPremium: false, status: 'free' });
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/user/subscription');
      const data = await res.json();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription({ isPremium: false, status: 'free' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, [session, sessionStatus]);

  return (
    <PremiumContext.Provider
      value={{
        ...subscription,
        isLoading,
        refreshSubscription: fetchSubscription,
      }}
    >
      {children}
    </PremiumContext.Provider>
  );
}

export function usePremium() {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error('usePremium must be used within a PremiumProvider');
  }
  return context;
}

// Configuration des features premium
export const PREMIUM_FEATURES = {
  maxFree3DElements: 20, // Limite de visualisations 3D en gratuit
  features: {
    full3DAccess: 'premium',
    noAds: 'premium',
    quiz: 'premium',
    pdfExport: 'premium',
    csvExport: 'premium',
    jsonExport: 'premium',
  } as const,
};

export const PRICING = {
  monthly: {
    price: 4.99,
    currency: '€',
    period: 'mois',
    priceId: process.env.NEXT_PUBLIC_STRIPE_PRICE_MONTHLY || '',
  },
  yearly: {
    price: 24.99,
    currency: '€',
    period: 'an',
    savings: '58%', // (4.99 * 12) - 24.99 = ~58% savings
    priceId: process.env.NEXT_PUBLIC_STRIPE_PRICE_YEARLY || '',
  },
};
